import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useTheme } from "../contexts/ThemeContext";
import { Check, Loader2, X } from "lucide-react";

export default function SubscriptionSuccessPage() {
  const [searchParams] = useSearchParams();
  const { api } = useAuth();
  const { theme } = useTheme();
  const isDark = theme === "dark";
  const navigate = useNavigate();
  const [status, setStatus] = useState("checking");
  const sessionId = searchParams.get("session_id");

  useEffect(() => {
    if (!sessionId) {
      setStatus("error");
      return;
    }
    let attempts = 0;
    const maxAttempts = 8;

    const poll = async () => {
      try {
        const res = await api().get(`/subscription/status/${sessionId}`);
        if (res.data.status === "paid" || res.data.is_premium) {
          setStatus("success");
          return;
        }
        if (res.data.status === "expired") {
          setStatus("expired");
          return;
        }
        attempts++;
        if (attempts < maxAttempts) {
          setTimeout(poll, 2500);
        } else {
          setStatus("timeout");
        }
      } catch {
        attempts++;
        if (attempts < maxAttempts) setTimeout(poll, 2500);
        else setStatus("error");
      }
    };

    poll();
  }, [sessionId, api]);

  const textPrimary = isDark ? "text-white" : "text-slate-900";

  return (
    <div
      className="min-h-screen flex flex-col items-center justify-center px-6"
      style={{ background: isDark ? "linear-gradient(to bottom, #0F172A, #312E81)" : "linear-gradient(to bottom, #f0f4ff, #e8e0f0)" }}
    >
      <div className="glass-card p-8 max-w-sm w-full text-center animate-fadeInUp">
        {status === "checking" && (
          <>
            <Loader2 size={48} className="text-amber-400 animate-spin mx-auto mb-4" />
            <h2 className={`font-heading text-2xl font-bold ${textPrimary}`}>Vérification du paiement...</h2>
            <p className={`text-sm mt-2 ${isDark ? "text-slate-400" : "text-slate-500"}`}>Un instant, nous vérifions votre paiement</p>
          </>
        )}
        {status === "success" && (
          <>
            <div className="w-16 h-16 rounded-full bg-emerald-500/20 flex items-center justify-center mx-auto mb-4">
              <Check size={32} className="text-emerald-400" />
            </div>
            <h2 className={`font-heading text-2xl font-bold ${textPrimary}`}>Bienvenue Premium !</h2>
            <p className={`text-sm mt-2 ${isDark ? "text-slate-400" : "text-slate-500"}`}>
              Vous avez maintenant accès à toutes les fonctionnalités DreamNoor sans publicité.
            </p>
            <button
              data-testid="back-to-app-button"
              onClick={() => navigate("/")}
              className="mt-6 btn-gold px-8 py-3 text-sm"
            >
              Retour à l'application
            </button>
          </>
        )}
        {(status === "error" || status === "expired" || status === "timeout") && (
          <>
            <div className="w-16 h-16 rounded-full bg-red-500/20 flex items-center justify-center mx-auto mb-4">
              <X size={32} className="text-red-400" />
            </div>
            <h2 className={`font-heading text-2xl font-bold ${textPrimary}`}>
              {status === "expired" ? "Session expirée" : "Erreur de paiement"}
            </h2>
            <p className={`text-sm mt-2 ${isDark ? "text-slate-400" : "text-slate-500"}`}>
              Le paiement n'a pas pu être confirmé. Veuillez réessayer.
            </p>
            <button
              data-testid="retry-button"
              onClick={() => navigate("/profile")}
              className="mt-6 btn-gold px-8 py-3 text-sm"
            >
              Réessayer
            </button>
          </>
        )}
      </div>
    </div>
  );
}
